<template>
  <div class="space-y-6">
    <div class="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg p-6 text-white">
      <div class="flex items-center gap-3">
        <router-link
          to="/subscriptions"
          class="inline-flex items-center justify-center sm:justify-start px-3 sm:px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
          aria-label="Volver"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          <span class="hidden sm:inline ml-2 text-sm font-semibold">Volver</span>
        </router-link>
        <div>
          <h1 class="text-2xl font-bold">Editar Plan</h1>
          <p class="text-amber-100 mt-1" v-if="plan">{{ plan.name }}</p>
          <p class="text-amber-200 mt-1 text-sm" v-else>Cargando...</p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center text-gray-500">
      Cargando plan...
    </div>

    <!-- Form -->
    <div v-else class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div class="bg-gradient-to-r from-yellow-500 to-orange-500 px-8 py-5">
            <h2 class="text-xl font-bold text-white flex items-center gap-3">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Información del plan
            </h2>
          </div>
          <div class="p-8">
            <form @submit.prevent="savePlan" class="space-y-8">

              <!-- Basic info -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Nombre del plan</label>
                  <input
                    v-model="form.name"
                    type="text"
                    required
                    placeholder="Ej: Premium Mensual"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Tipo de plan</label>
                  <select
                    v-model="form.type"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Precio (USD)</label>
                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                    <input
                      v-model="form.price"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      class="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Duración (días)</label>
                  <input
                    v-model="form.durationDays"
                    type="number"
                    min="0"
                    required
                    placeholder="0 = indefinida"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  />
                  <p class="text-xs text-gray-400 mt-1">0 = duración indefinida</p>
                </div>
              </div>

              <!-- Limits -->
              <div>
                <h3 class="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">Límites del plan</h3>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="bg-green-50 rounded-xl p-5 border border-green-100">
                    <label class="block text-sm font-semibold text-green-800 mb-2">Máx. mascotas</label>
                    <input
                      v-model="form.maxPets"
                      type="number"
                      min="1"
                      required
                      class="w-full px-3 py-2.5 border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 bg-white transition-all"
                    />
                  </div>
                  <div class="bg-blue-50 rounded-xl p-5 border border-blue-100">
                    <label class="block text-sm font-semibold text-blue-800 mb-2">Likes por día</label>
                    <input
                      v-model="form.likesPerDay"
                      type="number"
                      min="-1"
                      required
                      class="w-full px-3 py-2.5 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white transition-all"
                    />
                    <p class="text-xs text-blue-600 mt-1.5">-1 = ilimitados</p>
                  </div>
                  <div class="bg-purple-50 rounded-xl p-5 border border-purple-100">
                    <label class="block text-sm font-semibold text-purple-800 mb-2">Super-likes por día</label>
                    <input
                      v-model="form.superLikesPerDay"
                      type="number"
                      min="0"
                      required
                      class="w-full px-3 py-2.5 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white transition-all"
                    />
                  </div>
                </div>
              </div>

              <!-- Features -->
              <div>
                <h3 class="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">Características visibles al usuario</h3>
                <div class="space-y-3">
                  <div v-for="(_, idx) in form.features" :key="idx" class="flex gap-3 items-center">
                    <div class="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <span class="text-xs font-bold text-yellow-700">{{ idx + 1 }}</span>
                    </div>
                    <input
                      v-model="form.features[idx]"
                      class="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="Ej: Likes ilimitados"
                    />
                    <button
                      type="button"
                      @click="form.features.splice(idx, 1)"
                      class="w-9 h-9 flex items-center justify-center rounded-lg transition-opacity hover:opacity-80 flex-shrink-0"
                      style="background-color:#be123c;color:#fff"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="form.features.push('')"
                    class="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90"
                    style="background-color:#475569;color:#fff"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Agregar característica
                  </button>
                </div>
              </div>

              <!-- Plan status -->
              <div class="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-semibold text-gray-800">Estado del plan</p>
                    <p class="text-xs text-gray-500 mt-0.5">Los planes inactivos no son visibles para los usuarios</p>
                  </div>
                  <button
                    type="button"
                    @click="toggleStatus"
                    :disabled="toggling"
                    class="px-5 py-2 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60"
                    :style="plan?.isActive ? 'background-color:#be123c;color:#fff' : 'background-color:#16a34a;color:#fff'"
                  >{{ toggling ? '...' : (plan?.isActive ? 'Desactivar plan' : 'Activar plan') }}</button>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <router-link
                  to="/subscriptions"
                  class="px-6 py-2.5 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90"
                  style="background-color:#475569;color:#fff"
                >Cancelar</router-link>
                <button
                  type="submit"
                  :disabled="saving"
                  class="px-6 py-2.5 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60"
                  style="background-color:#1d4ed8;color:#fff"
                >{{ saving ? 'Guardando...' : 'Guardar cambios' }}</button>
              </div>
            </form>
          </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { subscriptionService } from '../services/subscriptionService'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const toggling = ref(false)
const plan = ref<any>(null)

const form = ref({
  name: '',
  type: 'premium',
  price: 0,
  durationDays: 30,
  maxPets: 5,
  likesPerDay: -1,
  superLikesPerDay: 5,
  features: [] as string[],
})

async function loadPlan() {
  loading.value = true
  try {
    const res = await subscriptionService.getPlan(String(route.params.id))
    plan.value = res.data
    const feats = res.data.features
    form.value = {
      name: res.data.name,
      type: res.data.type,
      price: res.data.price,
      durationDays: res.data.durationDays,
      maxPets: res.data.maxPets,
      likesPerDay: res.data.likesPerDay,
      superLikesPerDay: res.data.superLikesPerDay,
      features: Array.isArray(feats) ? [...feats] : (feats ? [feats] : []),
    }
  } catch {
    toast.error('No se pudo cargar el plan')
    router.push('/subscriptions')
  } finally {
    loading.value = false
  }
}

async function savePlan() {
  saving.value = true
  try {
    const payload = { ...form.value, features: form.value.features.filter((f) => f.trim() !== '') }
    await subscriptionService.updatePlan(String(route.params.id), payload)
    toast.success('Plan actualizado correctamente')
    router.push('/subscriptions')
  } catch {
    toast.error('Error al guardar los cambios')
  } finally {
    saving.value = false
  }
}

async function toggleStatus() {
  toggling.value = true
  try {
    await subscriptionService.togglePlan(String(route.params.id), !plan.value.isActive)
    plan.value.isActive = !plan.value.isActive
    toast.success(plan.value.isActive ? 'Plan activado' : 'Plan desactivado')
  } catch {
    toast.error('Error al cambiar el estado')
  } finally {
    toggling.value = false
  }
}

onMounted(loadPlan)
</script>
